<script setup lang="ts">
import { ElNotification } from 'element-plus'
import { confirmedEmail } from '@/components/goat/page/home/RegisterBox'
import { confirmPasswordValidator, onChangePassword, passwordValidator } from '@/compositions/validators'
import { useAuthStore } from '@/stores/auth'
import { ServiceNames, useGoatStore } from '@/stores/goat'
import { ModeEnum } from '@/types/enums'

const { t } = useI18n()
const loading = ref(false)

const { updateMode } = siteUtils
function capitalizeFirst(str: string) {
  return (str || '').charAt(0).toUpperCase() + (str || '').slice(1)
}
function handleRegisterResponse(status: string, token: string, email: string, serviceName = '') {
  const { contextGaRef, gaSections } = gaUtils
  contextGaRef.value = { gs_section: gaSections.registerForm }
  switch (status) {
    case 'ok':
    {
      const auth = useAuthStore()
      auth.login(token, email)
      const { gaLogin, gaLoginMethods, gaSections } = gaUtils
      gaLogin({ method: `${gaLoginMethods.goat}${serviceName?.length ? ',' : ''}${serviceName}`, gs_section: gaSections.registerForm })
      break }
    case 'confirm_email':
      ElNotification({
        title: t('register.notify.oneMoreStep.title'),
        message: t('register.notify.oneMoreStep.message'),
        type: 'success',
        duration: 0,
        offset: 100,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute'
      })
      confirmedEmail.value = email
      updateMode(ModeEnum.confirmEmail)
      break
    case 'in_use':
      ElNotification({
        title: t('register.notify.emailInUse.title'),
        message: t('register.notify.emailInUse.message'),
        type: 'warning',
        offset: 100,
        duration: 9000,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute'
      })
      updateMode(ModeEnum.login)
      break
    case 'accept_terms':
      if (token && serviceName) {
        const goat = useGoatStore()
        goat.updateGoatDetails({ serviceToken: token, serviceName })
        updateMode(ModeEnum.acceptTerms)
      } else {
        ElNotification({
          title: t('register.notify.acceptTermsRequired.title'),
          message: t('register.notify.acceptTermsRequired.message'),
          type: 'error',
          duration: 9000,
          offset: 100,
          appendTo: '#auth-wrapper',
          customClass: 'right-1/2 translate-x-1/2 large absolute'
        })
      }
      break
    case 'missing_email':
      ElNotification({
        title: t('register.notify.missingEmail.title', { service: capitalizeFirst(serviceName) }),
        type: 'error',
        offset: 100,
        duration: 9000,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute max-w-full'
      })
      break
    default:
      ElNotification({
        title: t('register.notify.unavailable.title'),
        message: t('register.notify.unavailable.message'),
        type: 'error',
        duration: 9000,
        offset: 100,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute'
      })
      updateMode(ModeEnum.login)
  }
}

function onSubmit(data: FormTypes.SubmitForm) {
  loading.value = true
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchFormErrors } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()
    ?.post(getGoatApiUrl('register'), { ...data.form })
    .then((response) => {
      handleRegisterResponse(response?.data?.status || '', response?.data?.token ?? '', response?.data?.email ?? (data.form?.email || ''))
    })
    .catch(catchFormErrors(data.externalErrors, { appendTo: '#auth-wrapper' }))
    .then(() => {
      loading.value = false
    })
}

function handleGoogleCredentialResponse(token: string) {
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchError } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()
    ?.post(getGoatApiUrl('gmail-login'), { token })
    .then((response) => {
      handleRegisterResponse(response?.data?.status || '', response?.data?.token ?? token, response?.data?.email ?? '', ServiceNames.gmail)
    })
    .catch(catchError({ appendTo: '#auth-wrapper' }))
}
function handleFbCredentialResponse(token: string) {
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchError } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()
    ?.post(getGoatApiUrl('facebook-login'), { token })
    .then((response) => {
      handleRegisterResponse(response?.data?.status || '', response?.data?.token ?? token, response?.data?.email ?? '', ServiceNames.facebook)
    })
    .catch(catchError({ appendTo: '#auth-wrapper' }))
}
// const isEnabledFb = import.meta.env.VITE_PROD !== 'on' || !!localStorage?.getItem('isEnabledFb')
const isEnabledFb = true
</script>

<template>
  <div class="box-form-wrapper mx-auto max-w-2xl big:max-w-(--breakpoint-xl) big:px-16 mt-5">
    <div
      class="box-form big:mt-10 mx-3 bg-white pt-10 big:pt-16 pb-10 px-2 big:px-16 rounded-2xl flex flex-col items-center"
    >
      <div class="mx-auto w-full max-w-md">
        <h1 class="text-lg big:text-2xl uppercase font-semibold text-center tracking-wider ">
          {{ t('register.title') }}
        </h1>
        <goat-form-component
          class="mt-6 big:mb-5 w-full"
          size="large"
          hide-required-asterisk
          @submit="onSubmit"
        >
          <template #default="{ submit, form, formRefer }">
            <goat-form-field-text-trim
              v-focus
              api-field-name="email"
              :placeholder="t('form.email')"
              :label="!!form?.email?.length ? t('form.email') : false"
              class="mt-1 big:mt-1.5 text-lg"
              required
              :rules="[{ type: 'email', trigger: 'blur', message: t('form.validation.notValidEmail') }]"
              @keyup.enter.stop.prevent="submit"
            />
            <goat-form-field-text-trim
              api-field-name="password"
              :placeholder="t('form.password')"
              :label="!!form?.password?.length ? t('form.password') : false"
              class="mt-1 big:mt-1.5 text-lg"
              type="password"
              show-password
              :rules="[passwordValidator()]"
              @input="onChangePassword(form, formRefer)"
              @keyup.enter.stop.prevent="submit"
            />
            <goat-form-field-text-trim
              api-field-name="confirmPassword"
              :placeholder="t('form.confirmPassword')"
              :label="!!form?.confirmPassword?.length ? t('form.confirmPassword') : false"
              class="mt-1 big:mt-1.5 text-lg"
              type="password"
              show-password
              :rules="[confirmPasswordValidator(form)]"
              @keyup.enter.stop.prevent="submit"
            />
            <goat-site-accept-terms-check-boxes />
            <goat-form-fields-group
              justify="space-between"
            >
              <el-button
                type="info"
                class="text-white rounded-lg uppercase text-lg w-[46%]"
                @click="updateMode(ModeEnum.login)"
              >
                {{ t('register.cancelBtn') }}
              </el-button>
              <el-button
                color="#29b300"
                class="text-white rounded-lg uppercase text-lg w-[46%]"
                size="large"
                :loading="loading"
                @click="submit()"
              >
                {{ t('register.confirmBtn') }}
              </el-button>
            </goat-form-fields-group>
            <goat-form-fields-group>
              <div class="flex justify-center items-center w-full mt-8 register">
                <goat-site-google-credentials
                  btn-text="signup_with"
                  :call-back="handleGoogleCredentialResponse"
                />
              </div>
            </goat-form-fields-group>
            <goat-form-fields-group>
              <div
                v-if="isEnabledFb"
                class="flex justify-center items-center w-full mt-8"
              >
                <goat-site-fb-credentials
                  :call-back="handleFbCredentialResponse"
                  :action="t('fbCredentials.registrationAction')"
                />
              </div>
            </goat-form-fields-group>
          </template>
        </goat-form-component>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
</style>
